import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, Eyebrow, ArrowLink } from "@/components/Reveal";

const FIRM_IMG =
  "https://images.unsplash.com/photo-1605308071366-cf1ea0bf34db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBsb25kb24lMjBidWlsZGluZyUyMGZhY2FkZSUyMGdsYXNzfGVufDB8fHx8MTc5MTgzMjQxMHww&ixlib=rb-4.1.0&q=85";

const PRINCIPLES = [
  {
    title: "Independent by Design",
    desc: "No lending book, no product quotas, no distribution targets. The firm's only agenda is the client's outcome.",
  },
  {
    title: "Relationship-Driven",
    desc: "A deliberately small number of significant mandates, each led personally and supported for the long term.",
  },
  {
    title: "Institutionally Minded",
    desc: "The rigour, discretion and transaction discipline expected by private equity firms, family offices and global asset managers.",
  },
];

export default function About() {
  return (
    <main className="bg-[#F6F4F0] text-[#1F1E1B] antialiased">
      <Navbar />

      <section data-testid="about-hero" className="pt-40 lg:pt-52 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>About PJG</Eyebrow>
            <h1 className="mt-8 font-serif text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-[#1F1E1B] leading-[1.05] max-w-5xl">
              An independent, <span className="italic">relationship-driven</span> advisory house.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 lg:pb-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <Reveal>
            <div className="relative">
              <div className="relative aspect-[4/5] max-h-[600px] w-full overflow-hidden border border-[#E3E0D8]">
                <img
                  src={FIRM_IMG}
                  alt="Historic stone architecture alongside modern glass in the City of London"
                  loading="lazy"
                  className="w-full h-full object-cover grayscale contrast-105 brightness-[0.95]"
                />
                <span className="absolute top-4 left-4 w-6 h-6 border-t border-l border-[#F6F4F0]/80" aria-hidden="true" />
                <span className="absolute top-4 right-4 w-6 h-6 border-t border-r border-[#F6F4F0]/80" aria-hidden="true" />
                <span className="absolute bottom-4 left-4 w-6 h-6 border-b border-l border-[#F6F4F0]/80" aria-hidden="true" />
                <span className="absolute bottom-4 right-4 w-6 h-6 border-b border-r border-[#F6F4F0]/80" aria-hidden="true" />
              </div>
              <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-[#1F1E1B] px-6 py-5">
                <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#A8A49B]">Established</p>
                <p className="mt-1 font-serif text-lg text-[#F6F4F0]">London · Mayfair</p>
              </div>
            </div>
          </Reveal>

          <div className="lg:pt-6">
            <Reveal delay={0.1}>
              <p className="text-base sm:text-lg font-light leading-relaxed text-[#1F1E1B]/85">
                PJG Capital Partners operates at the intersection of family wealth, institutional investment
                and major corporate transactions. The firm serves private families, family offices, asset
                managers, private equity firms, institutional investors and owners of substantial private
                assets.
              </p>
              <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-[#6E6A61]">
                We are advisory-first. The firm combines the relationships and personal service of a
                multi-family office with the transaction capabilities of a specialist investment advisory
                house — and it is deliberately neither a brokerage, a platform nor a product distributor.
              </p>
              <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-[#6E6A61]">
                Our approach emphasises discretion, detailed assessment of underlying assets and businesses,
                and an understanding of the commercial priorities of the investors, families and owners we
                serve.
              </p>
            </Reveal>

            <div className="mt-12 border-t border-[#E3E0D8]">
              {PRINCIPLES.map((p, i) => (
                <Reveal key={p.title} delay={0.15 + i * 0.07}>
                  <div className="py-8 border-b border-[#E3E0D8] flex gap-6" data-testid={`about-principle-${i}`}>
                    <span className="font-sans text-[11px] tracking-[0.24em] text-[#6E6A61] pt-1">0{i + 1}</span>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl font-light text-[#1F1E1B]">{p.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-[#6E6A61]">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="leadership-section" className="py-28 lg:py-40 bg-[#FFFFFF] border-y border-[#E3E0D8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Leadership</Eyebrow>
            <h2 className="mt-8 font-serif text-4xl sm:text-5xl font-light tracking-tight text-[#1F1E1B] leading-[1.08]">
              The <span className="italic">Founder.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 border border-[#E3E0D8] bg-[#FFFFFF] p-8 lg:p-14" data-testid="founder-card">
              <div className="grid sm:grid-cols-[220px_1fr] gap-10 items-start">
                <div className="relative w-44 sm:w-full aspect-[3/4] bg-[#F1EEE8] border border-[#E3E0D8] flex items-center justify-center" data-testid="founder-portrait">
                  <span className="font-serif text-6xl font-light text-[#1F1E1B]/25">JW</span>
                  <span className="absolute top-3 left-3 w-5 h-5 border-t border-l border-[#1F1E1B]/30" aria-hidden="true" />
                  <span className="absolute top-3 right-3 w-5 h-5 border-t border-r border-[#1F1E1B]/30" aria-hidden="true" />
                  <span className="absolute bottom-3 left-3 w-5 h-5 border-b border-l border-[#1F1E1B]/30" aria-hidden="true" />
                  <span className="absolute bottom-3 right-3 w-5 h-5 border-b border-r border-[#1F1E1B]/30" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-sans text-[10px] uppercase tracking-[0.28em] text-[#6E6A61]">
                    Founder & Managing Partner
                  </p>
                  <h3 className="mt-4 font-serif text-3xl sm:text-4xl font-light tracking-tight text-[#1F1E1B]">
                    Joshua Williams
                  </h3>
                  <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-[#1F1E1B]/85">
                    Joshua Williams founded PJG Capital Partners in 2019. Since establishing the firm, he
                    has discreetly advised on transactions with an aggregate value exceeding £2 billion,
                    spanning sports investments, private capital and real estate.
                  </p>
                  <p className="mt-5 text-sm sm:text-base font-light leading-relaxed text-[#6E6A61]">
                    His experience encompasses professional football club transactions, strategic capital
                    partnerships and significant real asset mandates, acting for private families, family
                    offices, institutional investors and business owners.
                  </p>
                  <p className="mt-5 text-sm sm:text-base font-light leading-relaxed text-[#6E6A61]">
                    He currently serves as Head of Sports Investments at Tangl Capital Partners, a position
                    held in a personal professional capacity. PJG Capital Partners and Tangl Capital
                    Partners are separate, unaffiliated firms.
                  </p>
                </div>
              </div>
              <p className="mt-10 pt-8 border-t border-[#E3E0D8] text-xs leading-relaxed text-[#6E6A61]">
                PJG Capital Partners is an independent firm and is not affiliated with, endorsed by, or
                connected to any other advisory business.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-14">
              <ArrowLink to="/contact" testid="about-contact-link">
                Contact the Firm
              </ArrowLink>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
